// paex-clock.js — Halloween clock - PE (Sasha 2026-10-09).
// ONE shared clock for every urgency surface: announcement bar - PE, Urgency
// Hero - PE, cart drawer / cart page, PDP countdown, mobile sticky line,
// cart reminder. Honest by construction:
//  - dates = today's delivery estimate for the shopper's saved location
//    (assets/paex-edd.js, same engine + same live proxy as the PDP/cart);
//  - "arrives before" decisions use the LATEST live date (AfterShip max);
//  - remote postal codes, US and unknown destinations never get a green
//    promise (verdict 'unknown');
//  - countdowns count to the warehouse cutoff minus a checkout buffer, tick
//    every second only while visible, never show 0, and refresh the dates at
//    the cutoff; a device clock off by > 2 min hides the countdowns.
//  - the PDP tile / sticky-bar timer is the exception (Sasha 2026-10-09):
//    it counts to the next DAILY cutoff (today's, else tomorrow's), every
//    day, ship day or not — bindCountdown({ daily: true }); the date beside
//    it still comes from the engine, so it may stay the same across a
//    weekend.
// Config: <script id="PaexClockConfig"> (snippets/paex-clock-config.liquid).
// API: window.paexClock (see bottom). Events on document:
//   paex:clock-ready (once), paex:clock-change (detail = state),
//   paex:clock-minute, paex:clock-cutoff.
// Consumers: paexClockReady(fn) runs fn now or once the clock is ready.

window.paexClockReady = window.paexClockReady || function (fn) {
  if (window.paexClock) { fn(window.paexClock); return; }
  document.addEventListener('paex:clock-ready', function () { fn(window.paexClock); }, { once: true });
};

(function () {
  if (window.paexClock) return;
  var node = document.getElementById('PaexClockConfig');
  if (!node || typeof paexEddEstimate !== 'function') return;
  var cfg;
  try { cfg = JSON.parse(node.textContent); } catch (error) { return; }
  var T = cfg.texts || {};
  var occ = cfg.occasion || {};
  var CA_REGIONS = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

  /* ---------------- time (warehouse timezone + device skew) ---------------- */

  var skewMs = 0;
  var skewKnown = false;
  var skewTried = false;
  try {
    var savedSkew = JSON.parse(sessionStorage.getItem('paex:clockskew') || 'null');
    if (savedSkew && typeof savedSkew.s === 'number') { skewMs = savedSkew.s; skewKnown = !savedSkew.f; skewTried = true; }
  } catch (error) { /* no cache */ }

  function nowMs() { return Date.now() - skewMs; }

  // device clock vs the shop's server (Date header of a no-store fetch); one
  // measure per session — resolution 1 s, plenty for a minutes-scale gate
  function measureSkew() {
    if (skewKnown || skewTried) return;
    skewTried = true;
    var sent = Date.now();
    fetch('/cart.js', { cache: 'no-store', credentials: 'same-origin' }).then(function (response) {
      var header = response.headers.get('date');
      var server = header ? Date.parse(header) : NaN;
      if (!Number.isFinite(server)) {
        // unreadable header: never retry this session (one request max)
        try { sessionStorage.setItem('paex:clockskew', JSON.stringify({ s: 0, f: 1 })); } catch (error) {}
        return;
      }
      var mid = (sent + Date.now()) / 2;
      skewMs = Math.round(mid - server);
      skewKnown = true;
      try { sessionStorage.setItem('paex:clockskew', JSON.stringify({ s: skewMs })); } catch (error) {}
      refresh();
    }).catch(function () {});
  }

  function clockTrusted() { return !skewKnown || Math.abs(skewMs) <= 120000; }

  var partsFormatter = null;
  function warehouseParts() {
    var tz = (cfg.edd && cfg.edd.tz) || 'America/Toronto';
    try {
      if (!partsFormatter) {
        partsFormatter = new Intl.DateTimeFormat('en-CA', {
          timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        });
      }
      var parts = partsFormatter.formatToParts(new Date(nowMs()));
      var map = {};
      parts.forEach(function (part) { map[part.type] = part.value; });
      return {
        y: Number(map.year), m: Number(map.month), d: Number(map.day),
        hour: Number(map.hour) % 24, minute: Number(map.minute), second: Number(map.second) || 0,
      };
    } catch (error) {
      var now = new Date(nowMs());
      return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate(), hour: now.getHours(), minute: now.getMinutes(), second: now.getSeconds() };
    }
  }

  function keyToDate(key) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
    return match ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12)) : null;
  }
  function dateToKey(date) {
    return paexEddKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
  }
  function todayKey() {
    var p = warehouseParts();
    return paexEddKey(p.y, p.m, p.d);
  }
  // Every clock estimate uses the cutoff MINUS the checkout buffer: the
  // countdown ends at that moment, so the dates and verdicts move with it
  // (review 2026-10-09 — no "order today" promise in the last buffer minutes).
  var bufferedCutoff = (function () {
    var raw = String((cfg.edd && cfg.edd.cutoff) || '12:00').trim();
    var match = /^(\d{1,2}):(\d{2})$/.exec(raw) || [0, '12', '00'];
    var minutes = Math.max(0, Number(match[1]) * 60 + Number(match[2]) - (Number(occ.bufferMin) || 0));
    return Math.floor(minutes / 60) + ':' + String(minutes % 60).padStart(2, '0');
  })();
  var parsedBase = null;
  function eddConfig(processing) {
    var base = cfg.edd || {};
    return {
      cutoff: bufferedCutoff, tz: base.tz, days: base.days, holidays: base.holidays, transit: base.transit,
      processing: processing == null ? base.processing : processing,
    };
  }
  function parsedConfig() {
    if (!parsedBase) parsedBase = paexEddParseConfig(eddConfig());
    return parsedBase;
  }

  // phase: off | countdown | eve | day
  function phase() {
    var today = todayKey();
    var date = occ.date || '';
    if (!date || (occ.start && today < occ.start) || today > date) return 'off';
    if (today === date) return 'day';
    var eve = keyToDate(date);
    eve.setUTCDate(eve.getUTCDate() - 1);
    if (today === dateToKey(eve)) return 'eve';
    return 'countdown';
  }

  function daysTo() {
    var target = keyToDate(occ.date);
    var today = keyToDate(todayKey());
    if (!target || !today) return 0;
    return Math.max(0, Math.round((target - today) / 86400000));
  }

  // ms left before the warehouse cutoff minus the checkout buffer (0 when the
  // warehouse no longer takes orders for today)
  function msToCutoff() {
    var parsed = parsedConfig();
    var p = warehouseParts();
    var today = new Date(Date.UTC(p.y, p.m - 1, p.d, 12));
    if (!paexEddIsShipDay(today, parsed)) return 0;
    var parts = parsed.cutoff.split(':');
    var cutoffSec = (Number(parts[0]) * 60 + Number(parts[1])) * 60;
    var nowSec = p.hour * 3600 + p.minute * 60 + p.second;
    return Math.max(0, (cutoffSec - nowSec) * 1000 - (nowMs() % 1000));
  }

  // ms to the next DAILY cutoff: today's (buffered) cutoff while it is
  // ahead, else tomorrow's — every day, ship day or not (Sasha 2026-10-09:
  // the PDP tile timer resets at noon daily; see the header)
  function msToDailyCutoff() {
    var parsed = parsedConfig();
    var p = warehouseParts();
    var parts = parsed.cutoff.split(':');
    var cutoffSec = (Number(parts[0]) * 60 + Number(parts[1])) * 60;
    var nowSec = p.hour * 3600 + p.minute * 60 + p.second;
    var left = cutoffSec - nowSec;
    if (left <= 0) left += 86400;
    var ms = left * 1000 - (nowMs() % 1000);
    // inside the last second the item is already expiring: the value the
    // re-bind sees must be tomorrow's, never a sub-second leftover
    if (ms < 1000) ms += 86400000;
    return ms;
  }

  /* ---------------- text ---------------- */

  function fill(template, vars) {
    return String(template || '').replace(/\{(\w+)\}/g, function (all, name) {
      return vars && vars[name] != null ? String(vars[name]) : all;
    });
  }
  function t(key, vars) { return fill(T[key] || '', vars); }

  function fmtDate(value) {
    var date = typeof value === 'string' ? keyToDate(value) : value;
    return date ? paexEddFormatDate(date) : '';
  }

  function fmtCountdown(ms) {
    var total = Math.max(1, Math.floor(ms / 1000));
    var h = Math.floor(total / 3600);
    var m = Math.floor((total % 3600) / 60);
    var s = total % 60;
    var pad = function (n) { return String(n).padStart(2, '0'); };
    if (h > 0) return t('countdown_hms', { h: h, mm: pad(m), ss: pad(s) });
    return t('countdown_ms', { m: m, ss: pad(s) });
  }

  /* ---------------- destination ---------------- */

  var gpsDest = null; // "Use my location" result (never written as typed)

  function cleanPostal(value) { return String(value || '').replace(/\s+/g, '').toUpperCase(); }
  function livePostal(value) {
    var clean = cleanPostal(value);
    if (/^[A-Z]\d[A-Z](\d[A-Z]\d)?$/.test(clean)) return clean;
    if (/^\d{5}(-\d{4})?$/.test(clean)) return clean;
    return null;
  }

  // Validates a typed postal code exactly like the PDP editor
  // (paex-delivery-method.js destForm): full CA postal or US ZIP.
  function validatePostal(raw) {
    var value = String(raw || '').trim().toUpperCase();
    if (/^\d{5}(-\d{4})?$/.test(value)) return { postal: value, region: 'US', place: value, country: 'US' };
    if (!/^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/.test(value)) return null;
    var region = typeof paexProvinceFromPostal === 'function' ? paexProvinceFromPostal(value) : null;
    if (!region) return null;
    var clean = cleanPostal(value);
    return { postal: clean, region: region, place: clean.slice(0, 3) + ' ' + clean.slice(3), country: 'CA' };
  }

  // remote = territories (X, Y) and rural FSAs (second character 0), the same
  // built-in rule as the shipping page (assets/paex-shipping.js zoneFor)
  function isRemotePostal(postal) {
    var raw = cleanPostal(postal);
    return /^[A-Z]\d/.test(raw) && (raw.charAt(1) === '0' || /^[XY]/.test(raw));
  }

  function destination() {
    var dest = typeof paexDestRead === 'function' ? paexDestRead() : null;
    if (dest && dest.region) {
      return {
        region: String(dest.region).toUpperCase(), postal: livePostal(dest.place), label: dest.place || dest.region,
        country: dest.region === 'US' ? 'US' : 'CA', source: dest.source || 'typed',
      };
    }
    if (gpsDest) return gpsDest;
    var customer = cfg.customer || {};
    if (customer.region) {
      return {
        region: String(customer.region).toUpperCase(), postal: livePostal(customer.zip),
        label: customer.zip || customer.region, country: CA_REGIONS.indexOf(String(customer.region).toUpperCase()) >= 0 ? 'CA' : 'US',
        source: 'account',
      };
    }
    var geo = typeof paexDeliveryReadGeo === 'function' ? paexDeliveryReadGeo() : null;
    if (geo && geo.regionCode) {
      return {
        region: String(geo.regionCode).toUpperCase(), postal: livePostal(geo.postal),
        label: geo.postal || geo.city || geo.regionName || geo.regionCode,
        country: String(geo.country || '').toUpperCase() || 'CA', source: 'geo',
      };
    }
    return { region: null, postal: null, label: '', country: '', source: 'none' };
  }

  // transit key for a destination, or null = no honest promise possible
  function transitKey(dest) {
    if (!dest || !dest.region) return null;
    if (dest.country && dest.country !== 'CA' && dest.country !== 'CAN') return null;
    if (CA_REGIONS.indexOf(dest.region) === -1) return null;
    // no postal code = no remote check possible: the territories are all
    // remote, so they never get a region-only promise
    if (!dest.postal && /^(NT|NU|YT)$/.test(dest.region)) return null;
    var parsed = parsedConfig();
    if (dest.postal && isRemotePostal(dest.postal)) {
      return parsed.transit[dest.region + '-R'] != null ? dest.region + '-R' : null;
    }
    if (parsed.transit[dest.region] != null) return dest.region;
    return parsed.transit['*'] != null ? '*' : null;
  }

  /* ---------------- estimates + verdicts ---------------- */

  var liveMemo = {};

  // Synchronous table estimate + async live upgrade. Returns
  // { key, base, est: {date, max, live}, region } where est.date is the key
  // SHOWN everywhere (same as the PDP tile / cart strip) and est.max the key
  // used for "arrives before" only.
  function estimateFor(dest, processing) {
    var key = transitKey(dest);
    if (!key) return null;
    var base = paexEddEstimate(key, eddConfig(processing));
    if (!base) return null;
    var out = { region: key, base: base, date: base.arrivalKey, max: base.arrivalKey, live: false };
    var live = cfg.live || {};
    var memoKey = (dest.postal || '') + '|' + (processing == null ? '' : processing) + '|' + dateToKey(base.orderDay);
    var memo = liveMemo[memoKey];
    if (memo && memo.date && paexEddLiveAccepts(base, memo.date)) {
      out.date = memo.date; out.max = memo.max || memo.date; out.live = true;
    }
    return out;
  }

  function upgradeLive(dest, processing) {
    var live = cfg.live || {};
    if (!live.on || live.source === 'app' || !dest || !dest.postal || typeof paexEddLiveFetchFull !== 'function') {
      return Promise.resolve(false);
    }
    var key = transitKey(dest);
    if (!key) return Promise.resolve(false);
    var base = paexEddEstimate(key, eddConfig(processing));
    if (!base) return Promise.resolve(false);
    var memoKey = dest.postal + '|' + (processing == null ? '' : processing) + '|' + dateToKey(base.orderDay);
    if (liveMemo[memoKey]) return Promise.resolve(false);
    var proc = processing == null ? (cfg.edd || {}).processing : processing;
    return paexEddLiveFetchFull(live.proxy || '/apps/paex-edd', dest.postal, proc).then(function (result) {
      liveMemo[memoKey] = result || { date: null };
      return Boolean(result && result.date && paexEddLiveAccepts(base, result.date));
    });
  }

  function verdictFor(dest, estimate) {
    if (!estimate) return 'unknown';
    var arriveBy = occ.arriveBy || '';
    if (!arriveBy) return 'unknown';
    if ((estimate.max || estimate.date) > arriveBy) return 'late';
    // last day: the transit table says today is the last order day, the
    // warehouse still takes today's orders, and the shown date IS the table
    // date (a faster live date must not be called "last day")
    var base = estimate.base;
    if (base.beforeCutoff && msToCutoff() > 0 && clockTrusted() &&
        (!estimate.live || estimate.date === base.arrivalKey)) {
      var orderBy = paexEddOrderBy(estimate.region, eddConfig(base.processingDays), arriveBy);
      if (orderBy && orderBy.key === dateToKey(base.orderDay)) return 'lastday';
    }
    return 'ontime';
  }

  // business days from today (warehouse) to an arrival key
  function businessDaysTo(arrivalKey) {
    var parsed = parsedConfig();
    var probe = keyToDate(todayKey());
    var end = keyToDate(arrivalKey);
    var count = 0;
    var guard = 0;
    while (probe < end && guard < 90) {
      probe = paexEddAddDays(probe, 1);
      guard += 1;
      if (paexEddIsShipDay(probe, parsed)) count += 1;
    }
    return count;
  }

  // Generic range for an unknown destination: fastest region → "*" (rest of
  // Canada), counted from today. allLate: even the fastest region misses
  // arrive-by; someLate: "*" misses (no range promised then).
  function range() {
    var parsed = parsedConfig();
    var keys = Object.keys(parsed.transit).filter(function (k) { return !/-R$/.test(k); });
    if (!keys.length) return null;
    var arriveBy = occ.arriveBy || '9999-12-31';
    var min = null; var max = null; var fastLate = true; var restLate = false;
    keys.forEach(function (k) {
      var est = paexEddEstimate(k, eddConfig());
      if (!est) return;
      var n = businessDaysTo(est.arrivalKey);
      if (min == null || n < min) min = n;
      if (max == null || n > max) max = n;
      if (est.arrivalKey <= arriveBy) fastLate = false;
      if (k === '*' && est.arrivalKey > arriveBy) restLate = true;
    });
    if (min == null) return null;
    return { min: min, max: max, allLate: fastLate, someLate: restLate };
  }

  /* ---------------- stores ---------------- */

  function preferredStore() {
    var chip = document.querySelector('paex-store-chip');
    var stores = chip && chip.data && chip.data.stores;
    var id = chip && chip.storeId;
    if (!stores || !id || !stores[id]) return null;
    var store = stores[id];
    var name = typeof paexDeliveryShortName === 'function' ? paexDeliveryShortName(store.name) : store.name;
    return { id: id, name: name, url: store.page || '' };
  }

  function openStoreDrawer() {
    var chip = document.querySelector('paex-store-chip');
    if (chip && typeof chip.openDrawer === 'function') { chip.openDrawer(); return true; }
    return false;
  }

  /* ---------------- countdowns (one shared ticker) ---------------- */

  var bound = [];
  var tickTimer = null;
  var observer = typeof IntersectionObserver === 'function'
    ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bound.forEach(function (item) { if (item.el === entry.target) item.visible = entry.isIntersecting; });
      });
      schedule();
    })
    : null;

  // minute-level reading ("15 h 22 min" / "8 min"), floor so it never
  // overstates the time left and never shows 0
  function fmtMinutes(ms) {
    var minute = Math.max(1, Math.floor(ms / 60000));
    var h = Math.floor(minute / 60);
    return h > 0 ? t('countdown_hm', { h: h, mm: String(minute % 60).padStart(2, '0') }) : (minute % 60) + ' min';
  }

  // bindCountdown(el, { onExpire, srEl, daily, minutes }) — el's text becomes
  // the live countdown (leaf text node only); returns false when no honest
  // countdown can run (past cutoff, untrusted device clock). daily: count to
  // the next daily cutoff instead (always ahead; onExpire re-binds for the
  // next day). minutes: show "{h} h {mm} min" instead of h/min/s (the PDP's
  // collapsed row, Sasha 2026-10-09 — stable width inside a sentence).
  function bindCountdown(el, options) {
    if (!el) return false;
    unbind(el);
    var daily = Boolean(options && options.daily);
    var ms = daily ? msToDailyCutoff() : msToCutoff();
    if (ms < 1000 || !clockTrusted()) return false;
    el.setAttribute('aria-hidden', 'true');
    el.classList.add('paex-clock-cd');
    var minutes = Boolean(options && options.minutes);
    var text = document.createTextNode(minutes ? fmtMinutes(ms) : fmtCountdown(ms));
    el.textContent = '';
    el.appendChild(text);
    var item = { el: el, text: text, visible: !observer, options: options || {}, minute: -1, daily: daily, minutes: minutes };
    bound.push(item);
    if (observer) observer.observe(el);
    paint(item, ms);
    schedule();
    return true;
  }

  function unbind(el) {
    bound = bound.filter(function (item) {
      if (item.el !== el) return true;
      if (observer) observer.unobserve(el);
      return false;
    });
  }

  function paint(item, ms) {
    var label = item.minutes ? fmtMinutes(ms) : fmtCountdown(ms);
    if (item.text.data !== label) item.text.data = label;
    item.el.classList.toggle('is-soon', ms <= (Number(occ.soonMin) || 30) * 60000);
    var minute = Math.floor(ms / 60000);
    if (item.options.srEl && minute !== item.minute) {
      item.minute = minute;
      var h = Math.floor(minute / 60);
      item.options.srEl.textContent = h > 0
        ? t('countdown_hm', { h: h, mm: String(minute % 60).padStart(2, '0') })
        : (minute % 60) + ' min';
    }
  }

  function tick() {
    tickTimer = null;
    bound = bound.filter(function (item) { return item.el.isConnected; });
    if (!bound.length) return;
    var ms = msToCutoff();
    var msDaily = msToDailyCutoff();
    var expired = bound.filter(function (item) { return (item.daily ? msDaily : ms) < 1000; });
    if (expired.length) {
      bound = bound.filter(function (item) { return expired.indexOf(item) === -1; });
      // the real cutoff just passed (a daily item ending at noon on a ship
      // day rides the same moment): dates and verdicts move now
      var cutoffHit = expired.some(function (item) { return !item.daily; });
      expired.forEach(function (item) {
        if (observer) observer.unobserve(item.el);
        // no longer bound: drop the marker + aria so a consumer's "still
        // ticking" check fails and it re-binds (or falls back) cleanly
        item.el.classList.remove('paex-clock-cd', 'is-soon');
        item.el.removeAttribute('aria-hidden');
        if (item.options.srEl) item.options.srEl.textContent = '';
        if (typeof item.options.onExpire === 'function') item.options.onExpire();
      });
      if (cutoffHit) {
        document.dispatchEvent(new CustomEvent('paex:clock-cutoff'));
        refresh();
      }
      if (!bound.length) return;
    }
    if (!document.hidden) {
      bound.forEach(function (item) { if (item.visible) paint(item, item.daily ? msDaily : ms); });
    }
    schedule();
  }

  function schedule() {
    if (tickTimer || !bound.length) return;
    var anyVisible = bound.some(function (item) { return item.visible; });
    var wait = !document.hidden && anyVisible ? 1000 - (nowMs() % 1000) + 5 : 5000;
    tickTimer = setTimeout(tick, wait);
  }

  /* ---------------- state + broadcast ---------------- */

  var state = null;
  var geoPending = true;
  var lastSignature = '';

  function compute() {
    var ph = phase();
    var dest = destination();
    if (dest.source !== 'none') geoPending = false;
    var estimate = ph === 'off' || !clockTrusted() ? null : estimateFor(dest, null);
    var verdict = ph === 'off' ? 'off' : (estimate ? verdictFor(dest, estimate) : 'unknown');
    return {
      phase: ph,
      days: daysTo(),
      today: todayKey(),
      destination: dest,
      pending: geoPending && dest.source === 'none',
      estimate: estimate,
      verdict: verdict,
      // the shown date = the SAME date the PDP tile and the cart strip show
      // (the first day of a live range; Sasha 2026-10-09: every surface must
      // agree). The latest day (estimate.max) still decides "arrives before".
      dateLabel: estimate ? fmtDate(estimate.date) : '',
      range: ph === 'off' ? null : range(),
      store: preferredStore(),
      timer: verdict === 'lastday',
      trusted: clockTrusted(),
    };
  }

  function emit(next) {
    state = next;
    var signature = [next.phase, next.days, next.verdict, next.destination.label, next.estimate && next.estimate.date,
      next.estimate && next.estimate.max, next.range && next.range.min, next.range && next.range.max,
      next.store && next.store.id, next.pending].join('|');
    if (signature === lastSignature) return;
    lastSignature = signature;
    document.dispatchEvent(new CustomEvent('paex:clock-change', { detail: next }));
  }

  var refreshTimer = null;
  var cutoffTimer = null;
  // one-shot refresh at the (buffered) cutoff, so an open tab moves its dates
  // the second the countdown ends — not up to a minute later
  function armCutoff() {
    clearTimeout(cutoffTimer);
    var ms = msToCutoff();
    if (ms > 0 && ms < 86400000) cutoffTimer = setTimeout(refresh, ms + 500);
  }
  function refresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      armCutoff();
      var next = compute();
      emit(next);
      if (next.phase !== 'off' && next.destination.postal) {
        upgradeLive(next.destination, null).then(function (changed) { if (changed) emit(compute()); });
      }
    }, 50);
  }

  // per-surface estimate with its own processing days (cart lines, PDP)
  function estimate(options) {
    var opts = options || {};
    var dest = opts.destination || destination();
    var processing = opts.processing == null ? null : opts.processing;
    var est = clockTrusted() ? estimateFor(dest, processing) : null;
    var result = { destination: dest, estimate: est, verdict: est ? verdictFor(dest, est) : 'unknown' };
    if (opts.live === false || !dest.postal) return Promise.resolve(result);
    return upgradeLive(dest, processing).then(function () {
      var upgraded = clockTrusted() ? estimateFor(dest, processing) : null;
      return { destination: dest, estimate: upgraded, verdict: upgraded ? verdictFor(dest, upgraded) : 'unknown' };
    });
  }

  /* ---------------- postal sheet (lazy) ---------------- */

  var sheet = null;
  var sheetTrigger = null;
  var sheetCloseTimer = null;
  var sheetScrollLocked = false;

  var sheetCssReady = null;
  function sheetCss(tpl) {
    if (!sheetCssReady) {
      sheetCssReady = new Promise(function (resolve) {
        if (!tpl.dataset.css) { resolve(); return; }
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = tpl.dataset.css;
        link.setAttribute('data-paex-postal-css', '');
        link.onload = link.onerror = function () { resolve(); };
        document.head.appendChild(link);
        setTimeout(resolve, 3000); // never block the sheet on a slow CSS
      });
    }
    return sheetCssReady;
  }

  function ensureSheet() {
    if (sheet) return sheet;
    var tpl = document.getElementById('PaexPostalSheetTpl');
    if (!tpl) return null;
    sheetCss(tpl);
    document.body.appendChild(tpl.content.cloneNode(true));
    sheet = document.querySelector('[data-paex-postal-sheet]');
    var form = sheet.querySelector('[data-pps-form]');
    var input = sheet.querySelector('[data-pps-input]');
    var error = sheet.querySelector('[data-pps-error]');
    var gps = sheet.querySelector('[data-pps-gps]');
    sheet.querySelectorAll('[data-pps-close]').forEach(function (button) {
      button.addEventListener('click', closeSheet);
    });
    sheet.addEventListener('keyup', function (event) {
      if (event.key === 'Escape') { event.stopPropagation(); closeSheet(); }
    });
    ['pointerenter', 'focusin'].forEach(function (type) {
      sheet.addEventListener(type, function () { clearTimeout(sheetCloseTimer); });
    });
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var valid = validatePostal(input.value);
      if (!valid) {
        input.setAttribute('aria-invalid', 'true');
        error.textContent = t('sheet_error');
        error.hidden = false;
        input.focus();
        return;
      }
      input.removeAttribute('aria-invalid');
      error.hidden = true;
      gpsDest = null;
      writeDest({ region: valid.region, place: valid.place }, valid.postal);
    });
    if (gps) {
      if (!navigator.geolocation) gps.hidden = true;
      gps.addEventListener('click', function () {
        gps.setAttribute('aria-busy', 'true');
        navigator.geolocation.getCurrentPosition(function (position) {
          gps.removeAttribute('aria-busy');
          nearestFsa(position.coords.latitude, position.coords.longitude).then(function (fsa) {
            var region = fsa && typeof paexProvinceFromPostal === 'function' ? paexProvinceFromPostal(fsa) : null;
            if (!region) { error.textContent = t('sheet_gps_fail'); error.hidden = false; return; }
            error.hidden = true;
            writeDest({ region: region, place: fsa, lat: position.coords.latitude, lng: position.coords.longitude, source: 'gps' }, fsa);
          });
        }, function () {
          gps.removeAttribute('aria-busy');
          error.textContent = t('sheet_gps_fail');
          error.hidden = false;
        }, { timeout: 8000, maximumAge: 600000 });
      });
    }
    return sheet;
  }

  var fsaPromise = null;
  function fsaTable() {
    if (!fsaPromise) {
      fsaPromise = cfg.fsaUrl
        ? fetch(cfg.fsaUrl).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; })
        : Promise.resolve(null);
    }
    return fsaPromise;
  }
  function nearestFsa(lat, lng) {
    return fsaTable().then(function (table) {
      var map = table && table.fsa;
      if (!map) return null;
      var best = null; var bestKm = null;
      Object.keys(map).forEach(function (fsa) {
        if (fsa.length !== 3) return;
        var c = map[fsa];
        var km = typeof paexDeliveryDistanceKm === 'function' ? paexDeliveryDistanceKm(lat, lng, c[0], c[1]) : Math.hypot(lat - c[0], lng - c[1]) * 111;
        if (bestKm == null || km < bestKm) { bestKm = km; best = fsa; }
      });
      return bestKm != null && bestKm <= 25 ? best : null;
    });
  }

  function writeDest(dest, postal) {
    if (postal && !dest.postal) dest.postal = postal; // zone input for the free shipping offer (2026-10-19)
    fsaTable().then(function (table) {
      var map = table && table.fsa;
      var c = map && (map[postal.slice(0, 3)] || map[postal.slice(0, 2)]);
      if (c && dest.lat == null) { dest.lat = c[0]; dest.lng = c[1]; }
      if (typeof paexDestWrite === 'function') paexDestWrite(dest);
      showResult();
    });
  }

  function showResult() {
    var box = sheet && sheet.querySelector('[data-pps-result]');
    if (!box) return;
    box.hidden = false;
    estimate({}).then(function (result) {
      var dest = result.destination;
      var est = result.estimate;
      box.querySelector('[data-pps-place]').textContent = t('result_place', { place: dest.label });
      box.querySelector('[data-pps-date]').textContent = est ? t('result_date', { date: fmtDate(est.date) }) : t('remote');
      var verdictEl = box.querySelector('[data-pps-verdict]');
      verdictEl.textContent = result.verdict === 'late' ? t('late') : (result.verdict === 'unknown' ? '' : t('on_time'));
      verdictEl.setAttribute('data-tone', result.verdict === 'late' ? 'warn' : 'ok');
      verdictEl.hidden = !verdictEl.textContent;
      clearTimeout(sheetCloseTimer);
      sheetCloseTimer = setTimeout(function () {
        if (!sheet.matches(':hover') && !sheet.contains(document.activeElement)) closeSheet();
      }, 2000);
    });
  }

  function openPostal(trigger) {
    var tpl = document.getElementById('PaexPostalSheetTpl');
    if (!tpl) return;
    var opener = trigger || document.activeElement;
    // first open waits for the lazy stylesheet: focusing an unstyled (in-flow)
    // sheet scrolled the page to the footer (vault QA 2026-10-09)
    sheetCss(tpl).then(function () { showSheet(opener); });
  }

  function showSheet(trigger) {
    var element = ensureSheet();
    if (!element) return;
    sheetTrigger = trigger;
    var panel = element.querySelector('[data-pps-panel]');
    element.querySelector('[data-pps-result]').hidden = true;
    element.querySelector('[data-pps-error]').hidden = true;
    element.hidden = false;
    // desktop: popover under the trigger; mobile: bottom sheet (CSS)
    if (window.matchMedia('(min-width: 750px)').matches && sheetTrigger && sheetTrigger.getBoundingClientRect) {
      var rect = sheetTrigger.getBoundingClientRect();
      panel.style.top = Math.round(rect.bottom + 8) + 'px';
      panel.style.left = Math.round(Math.min(Math.max(12, rect.left + rect.width / 2 - 180), window.innerWidth - 372)) + 'px';
    } else {
      panel.style.top = '';
      panel.style.left = '';
    }
    var input = element.querySelector('[data-pps-input]');
    // webvista is a top-level const in global.js (not on window)
    var wv = typeof webvista !== 'undefined' ? webvista : null;
    if (wv && typeof wv.trapFocus === 'function') wv.trapFocus(panel, input);
    else input.focus({ preventScroll: true });
    if (wv && typeof wv.disablePageScroll === 'function' && !window.matchMedia('(min-width: 750px)').matches) {
      wv.disablePageScroll();
      sheetScrollLocked = true;
    }
    document.dispatchEvent(new CustomEvent('paex:postal-open', { detail: { trigger: sheetTrigger } }));
  }

  function closeSheet() {
    if (!sheet || sheet.hidden) return;
    clearTimeout(sheetCloseTimer);
    sheet.hidden = true;
    var wv = typeof webvista !== 'undefined' ? webvista : null;
    if (sheetScrollLocked && wv && typeof wv.enablePageScroll === 'function') wv.enablePageScroll();
    sheetScrollLocked = false;
    // listeners (e.g. the bar rotator) may reveal the trigger before focus returns
    document.dispatchEvent(new CustomEvent('paex:postal-close', { detail: { trigger: sheetTrigger } }));
    if (wv && typeof wv.removeTrapFocus === 'function') wv.removeTrapFocus(sheetTrigger);
    else if (sheetTrigger && sheetTrigger.focus) sheetTrigger.focus();
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest && event.target.closest('[data-paex-postal-open]');
    if (!opener) return;
    event.preventDefault();
    openPostal(opener);
  });

  /* ---------------- wiring ---------------- */

  ['paex:geo-ready', 'paex:dest-change', 'paex:store-change', 'paex:clock-cutoff'].forEach(function (type) {
    document.addEventListener(type, function () {
      if (type === 'paex:geo-ready') geoPending = false;
      refresh();
    });
  });
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) { refresh(); schedule(); }
  });
  // the geo wait ends after 3 s even when nothing answers
  setTimeout(function () { geoPending = false; refresh(); }, 3000);
  // minute heartbeat: day / cutoff-slot rollovers + consumers' slow updates
  var lastSlot = '';
  setInterval(function () {
    if (document.hidden) return;
    var slot = todayKey() + (msToCutoff() > 0 ? 'a' : 'p');
    if (slot !== lastSlot) { lastSlot = slot; refresh(); }
    document.dispatchEvent(new CustomEvent('paex:clock-minute'));
  }, 60000);

  window.paexClock = {
    config: cfg,
    t: t,
    fill: fill,
    fmtDate: fmtDate,
    fmtCountdown: fmtCountdown,
    phase: phase,
    daysTo: daysTo,
    todayKey: todayKey,
    msToCutoff: msToCutoff,
    msToDailyCutoff: msToDailyCutoff,
    destination: destination,
    validatePostal: validatePostal,
    estimate: estimate,
    range: range,
    preferredStore: preferredStore,
    openStoreDrawer: openStoreDrawer,
    bindCountdown: bindCountdown,
    unbind: unbind,
    openPostal: openPostal,
    refresh: refresh,
    get state() { return state; },
  };
  lastSlot = todayKey() + (msToCutoff() > 0 ? 'a' : 'p');
  state = compute();
  lastSignature = '';
  emit(state);
  armCutoff();
  document.dispatchEvent(new CustomEvent('paex:clock-ready', { detail: state }));
  if (state.destination.postal && state.phase !== 'off') {
    upgradeLive(state.destination, null).then(function (changed) { if (changed) emit(compute()); });
  }
  setTimeout(measureSkew, 2500);
})();
